// @ts-strict-ignore
import * as React from 'react';
import Space from 'antd/lib/space';
import Divider from 'antd/lib/divider';
import Text from 'antd/lib/typography/Text';
import Tooltip from 'antd/lib/tooltip';
import { InfoCircleOutlined, ProfileOutlined, SafetyCertificateFilled } from '@ant-design/icons';
import {
    AppDistributionService,
    BinaryBuildDetails,
    BuildArtifact,
    BuildArtifactType,
    IntegrationCapability,
    IntegrationInstallationId,
    StabilityMonitoringService,
} from '@/types';
import { antGrey, runwayColors, titleStyle } from '@/components/styles/globalStyles';
import {
    artifactTypeDescription,
    displayNameForAppleCertificateType,
    displayTextForProvisioningProfileType,
} from '@/helpers/displayHelpers';
import { DatetimeFormat } from '@/timeHelpers';
import { dayjs } from '@/extendedDayjs';
import { UploadStatusSection } from './UploadStatusSection';
import { ArtifactUploadInfoTag } from './ArtifactsModule';

interface BinaryBuildDetailItemProps {
    header: string;
    body: string | React.ReactNode;
    bodyStyle?: React.CSSProperties;
}

export const BinaryBuildDetailItem: React.FC<BinaryBuildDetailItemProps> = ({ header, body, bodyStyle }) => {
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: '12px 20px',
            }}
        >
            <Text
                style={{
                    ...titleStyle,
                    color: antGrey.grey7,
                    fontSize: 10,
                }}
            >
                {header}
            </Text>
            {typeof body === 'string' ?
                <Text
                    style={{
                        color: antGrey.grey8,
                        textAlign: 'left',
                        ...bodyStyle,
                    }}
                >
                    {body}
                </Text>
            :   body}
        </div>
    );
};

export const BinaryBuildDetailSeparator: React.FC = () => {
    return <div style={{ width: '1px', backgroundColor: antGrey.grey4, marginTop: 12, marginBottom: 12 }} />;
};

interface BinaryBuildDetailsViewProps {
    artifact: BuildArtifact;
    /** When true, renders the upload status section. Defaults to false. */
    showUploadStatus?: boolean;
    /** Required when showUploadStatus is true - map of distribution services by installation ID */
    distributionServices?: Map<IntegrationInstallationId, AppDistributionService>;
    /** Required when showUploadStatus is true - map of app identifiers by capability */
    appIdentifiers?: Map<IntegrationCapability, string>;
    /** Optional stability monitoring service for dSYM upload status */
    stabilityMonitoring?: StabilityMonitoringService;
}

export const BinaryBuildDetailsView: React.FC<BinaryBuildDetailsViewProps> = ({
    artifact,
    showUploadStatus = false,
    distributionServices,
    appIdentifiers,
    stabilityMonitoring,
}) => {
    const details = artifact.binaryBuildDetails;
    const hasAppStoreInfos = artifact.appStoreInfos && Object.keys(artifact.appStoreInfos).length > 0;
    const hasDsymUploadInfo = !!artifact.dsymUploadInfo;
    const hasUploadStatus = hasAppStoreInfos || hasDsymUploadInfo;

    const ios =
        details ?
            [
                BuildArtifactType.IOSAdHoc,
                BuildArtifactType.IOSDevelopment,
                BuildArtifactType.IOSEnterprise,
                BuildArtifactType.IOSAppStore,
            ].includes(details.artifactType)
        :   false;

    const android =
        details ?
            [
                BuildArtifactType.AndroidDebuggableSigned,
                BuildArtifactType.AndroidDebuggableUnsigned,
                BuildArtifactType.AndroidReleaseSigned,
                BuildArtifactType.AndroidReleaseUnsigned,
                BuildArtifactType.AndroidAAB,
            ].includes(details.artifactType)
        :   false;

    const renderCertificateItem = (details: BinaryBuildDetails) => {
        const certificates = details.provisioningProfile?.certificates;

        if (certificates && certificates.length > 0) {
            const certificate = certificates[0];

            return (
                <>
                    <BinaryBuildDetailSeparator />
                    <BinaryBuildDetailItem
                        header="CERTIFICATE TYPE"
                        body={
                            <Space align="center">
                                <SafetyCertificateFilled style={{ color: antGrey.grey6 }} />
                                <Text style={{ color: antGrey.grey8 }}>
                                    {displayNameForAppleCertificateType(certificate.certificateType)}
                                </Text>
                                <Tooltip
                                    title={
                                        <Space direction="vertical">
                                            <Text style={{ color: 'white' }}>
                                                <strong>Certificate name:</strong> {certificate.name}
                                            </Text>
                                            <Text style={{ color: 'white' }}>
                                                <strong>Expiry:</strong>{' '}
                                                {dayjs(certificate.expirationDate).format(
                                                    DatetimeFormat.ShortMonthDateYear,
                                                )}
                                            </Text>
                                        </Space>
                                    }
                                >
                                    <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                                </Tooltip>
                            </Space>
                        }
                    />
                </>
            );
        }

        return null;
    };

    const renderUploadStatus = () => {
        if (!showUploadStatus || !hasUploadStatus) {
            return null;
        }

        return (
            <UploadStatusSection style={{ marginTop: 8 }}>
                <Space
                    size={8}
                    split={
                        <Divider
                            type="vertical"
                            style={{ backgroundColor: antGrey.grey5, height: 22, marginTop: '-10px' }}
                        />
                    }
                    wrap
                >
                    {hasAppStoreInfos &&
                        Object.entries(artifact.appStoreInfos).map(([key, appStoreInfo]) => {
                            const service = distributionServices?.get(key);
                            if (!service) {
                                return null;
                            }
                            return (
                                <ArtifactUploadInfoTag
                                    key={key}
                                    status={appStoreInfo.uploadStatus}
                                    appIdentifiers={appIdentifiers}
                                    service={service}
                                    errorMessage={appStoreInfo.errorMessage}
                                    trackName={appStoreInfo.trackName}
                                />
                            );
                        })}
                    {hasDsymUploadInfo && stabilityMonitoring && (
                        <ArtifactUploadInfoTag
                            status={artifact.dsymUploadInfo.uploadStatus}
                            appIdentifiers={appIdentifiers}
                            service={stabilityMonitoring}
                            stabilityIntegrationProjectName={artifact.dsymUploadInfo.projectName}
                            errorMessage={artifact.dsymUploadInfo.errorMessage}
                        />
                    )}
                </Space>
            </UploadStatusSection>
        );
    };

    // If no binary details but has upload status, show only upload status
    if (!details) {
        if (showUploadStatus && hasUploadStatus) {
            return <div style={{ backgroundColor: antGrey.grey2, minWidth: 300 }}>{renderUploadStatus()}</div>;
        }
        return null;
    }

    return (
        <div style={{ backgroundColor: antGrey.grey2 }}>
            <div
                style={{
                    width: '100%',
                    backgroundColor: runwayColors.white,
                    display: 'flex',
                    flexWrap: 'wrap',
                    flex: 1,
                    gap: 1,
                    borderRadius: 8,
                    borderWidth: 1,
                    borderStyle: 'solid',
                    borderColor: antGrey.grey4,
                }}
            >
                {details.appName && <BinaryBuildDetailItem header="APP NAME" body={details.appName} />}
                <BinaryBuildDetailSeparator />
                <BinaryBuildDetailItem
                    header={android ? 'PACKAGE NAME' : 'BUNDLE IDENTIFIER'}
                    body={details.bundleName}
                    bodyStyle={{ fontFamily: 'monospace' }}
                />
                <BinaryBuildDetailSeparator />
                <BinaryBuildDetailItem
                    header={android ? 'VERSION NAME' : 'VERSION STRING'}
                    body={details.versionString}
                    bodyStyle={{ fontFamily: 'monospace' }}
                />
                <BinaryBuildDetailSeparator />
                <BinaryBuildDetailItem
                    header={android ? 'VERSION CODE' : 'BUILD NUMBER'}
                    body={details.buildIdentifier}
                    bodyStyle={{ fontFamily: 'monospace' }}
                />
                {android && (
                    <>
                        <BinaryBuildDetailSeparator />
                        <BinaryBuildDetailItem
                            header="BUILD TYPE"
                            body={artifactTypeDescription(details.artifactType)}
                        />
                    </>
                )}
                {ios && details.provisioningProfile && (
                    <>
                        <BinaryBuildDetailSeparator />
                        <BinaryBuildDetailItem
                            header="PROFILE TYPE"
                            body={
                                <Space align="center">
                                    <ProfileOutlined style={{ color: antGrey.grey6 }} />
                                    <Text style={{ color: antGrey.grey8 }}>
                                        {displayTextForProvisioningProfileType(details.provisioningProfile.profileType)}
                                    </Text>
                                    <Tooltip
                                        title={
                                            <Space direction="vertical">
                                                <Text style={{ color: 'white' }}>
                                                    <strong>Profile name:</strong> {details.provisioningProfile.name}
                                                </Text>
                                                <Text style={{ color: 'white' }}>
                                                    <strong>Expiry:</strong>{' '}
                                                    {dayjs(details.provisioningProfile.expirationDate).format(
                                                        DatetimeFormat.ShortMonthDateYear,
                                                    )}
                                                </Text>
                                            </Space>
                                        }
                                    >
                                        <InfoCircleOutlined style={{ color: antGrey.grey6 }} />
                                    </Tooltip>
                                </Space>
                            }
                        />
                    </>
                )}
                {ios && renderCertificateItem(details)}
            </div>
            {renderUploadStatus()}
        </div>
    );
};
